// import { v4 as uuidv4 } from 'uuid'
import { request } from '@remote/api/request'
import { store } from '@remote/store'
import type { IErrorRes, IOptions } from '@remote/type'
import type { IGetScrapeSettingsResult, IHttpScrapePathStruct } from '@remote/type/cinema'

function getHeziDeviceId (): string {
  const deviceId = store.state.curDeviceRunner.params?.target
  return deviceId || ''
}

interface IHeziRequestResponse<T> {
  code: number
  ret: number
  data: T
  msg?: string
}
type IRequestCommonResponse<T> = [IErrorRes | null, IHeziRequestResponse<T>]
const $http = {
  get: async <T>(url: string, params?: object, traceId = '') => {
    const heziDeviceId = getHeziDeviceId()
    const headers: IOptions['headers'] = { 'Target': heziDeviceId }
    if (traceId) {
      headers['X-Trace-Id'] = traceId
    }
    const options: IOptions = {
      apiEnv: 'heziProduction',
      method: 'GET',
      headers,
    }
    return request(url, params, options).then((response: IRequestCommonResponse<T>) => {
      const [err, result] = response
      if (err) {
        const { error, error_description, msg } = err
        const errMessage = `${msg}(#${error}: ${error_description})`
        throw new Error(errMessage)
      }

      return result
    })
  },
  post: async <T>(url: string, data?: object, traceId = '') => {
    const heziDeviceId = getHeziDeviceId()
    const headers: IOptions['headers'] = { 'Target': heziDeviceId }
    if (traceId) {
      headers['X-Trace-Id'] = traceId
    }
    const options: IOptions = {
      apiEnv: 'heziProduction',
      method: 'POST',
      headers,
    }
    return request(url, data, options).then((response: IRequestCommonResponse<T>) => {
      const [err, result] = response
      if (err) {
        const { error = '', error_description = '', msg = '' } = err
        const errMessage = `${msg}(#${error}: ${error_description})`
        throw new Error(errMessage)
      }

      return result
    })
  },
}

export async function getScrapePathSettings (traceId = '') {
  const url = '/api/v1/user/scrape_path'
  return $http.get<IGetScrapeSettingsResult>(url, {}, traceId).then(response => {
    if (!response) throw new Error('timeout')
    const { data, code } = response
    if (code !== 200) {
      throw new Error(`错误码：${code}`)
    }

    const { is_scrape, is_all_scrape } = data
    const list = Array.isArray(data.list) ? data.list : []
    const isScrape = is_scrape === 1
    const isScrapeAll = is_all_scrape === 1
    return { list, isScrape, isScrapeAll }
  })
}

interface IUpdateScrapeSettingsParams {
  /** 是否开启刮削 */
  isScrape?: boolean
  /** 是否全盘刮削 */
  isAllScrape?: boolean
}
/** 更新刮削开关配置（是否开启刮削、是否全盘刮削） */
export function updateScrapeSettings (settings: IUpdateScrapeSettingsParams, traceId = '') {
  const is_scrape = typeof settings.isScrape === 'boolean' ? (settings.isScrape ? '1' : '0') : undefined
  const is_all_scrape = typeof settings.isAllScrape === 'boolean' ? (settings.isAllScrape ? '1' : '0') : undefined
  const url = '/api/v1/user/edit'
  return $http.post<null>(url, { is_scrape, is_all_scrape }, traceId).then(response => {
    const { code, data, msg } = response
    if (code !== 200) {
      throw new Error(msg)
    }
    return data
  })
}

/**
 * 删除刮削路径
 * @param pathId 刮削服务在服务器上的数据id
 */
export async function deleteScrapePath (pathId: string | string[], traceId = '') {
  const pathIds = Array.isArray(pathId) ? pathId : [pathId]
  const url = '/api/v1/user/delete_scrape_path'
  return $http.post<null>(url, { path_ids: pathIds }, traceId).then(response => {
    if (!response) throw new Error('timeout')
    const { code, data, msg = '删除失败' } = response
    if (code !== 200) {
      throw new Error(code ? `出错了，错误码(${code})` : msg)
    }

    return data
  })
}

interface IAddScrapePathParams {
  paths: Array<Omit<IHttpScrapePathStruct, 'path_id'>>
}
export async function addScrapePath (params: IAddScrapePathParams, traceId = '') {
  const { paths } = params
  const url = '/api/v1/user/set_scrape_path'
  return $http.post<{ paths: IHttpScrapePathStruct[] }>(url, { paths }, traceId).then(response => {
    if (!response) throw new Error('timeout')
    const { code, data, msg = '添加失败' } = response
    if (code !== 200) {
      throw new Error(code ? `出错了，错误码(${code})` : msg)
    }

    const paths = Array.isArray(data.paths) ? data.paths : []
    return { paths }
  })
}
